import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag, Button, Popconfirm, message } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { getReportSchedules, deleteReportSchedule } from '../../services/regulatoryReportingService';
import ScheduleFormModal from './ScheduleFormModal';

const REPORT_TYPE_LABELS = {
  CBN_RETURNS: 'CBN Returns',
  NDIC_RETURNS: 'NDIC Returns',
  AML_CTR: 'AML — CTR',
  AML_SAR: 'AML — SAR',
  AML_STR: 'AML — STR',
  NFIU_REPORT: 'NFIU Report',
  EFCC_REPORT: 'EFCC Compliance',
  CREDIT_BUREAU: 'Credit Bureau',
  CAPITAL_ADEQUACY: 'Capital Adequacy',
  LIQUIDITY_REPORT: 'Liquidity Report',
  PRUDENTIAL_RETURNS: 'Prudential Returns',
  RISK_ASSESSMENT: 'Risk Assessment',
  KYC_COMPLIANCE: 'KYC Compliance',
  TRANSACTION_MONITORING: 'Transaction Monitoring',
  CUSTOM: 'Custom',
};

const FREQ_COLORS = {
  DAILY: 'orange',
  WEEKLY: 'blue',
  MONTHLY: 'purple',
  QUARTERLY: 'cyan',
};

const SchedulesTab = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [modalVisible, setModalVisible] = useState(false);
  const [editRecord, setEditRecord] = useState(null);

  const fetchSchedules = useCallback(async (page = 1, size = 10) => {
    setLoading(true);
    try {
      const res = await getReportSchedules({ page: page - 1, size });
      if (res?.status) {
        setSchedules(res.data?.content || []);
        setPagination({ current: page, pageSize: size, total: res.data?.totalElements || 0 });
      }
    } catch (err) {
      console.error('Failed to fetch schedules:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  const handleTableChange = (pag) => fetchSchedules(pag.current, pag.pageSize);

  const handleDelete = async (scheduleId) => {
    try {
      const res = await deleteReportSchedule(scheduleId);
      if (res?.status) {
        message.success('Schedule deleted');
        fetchSchedules(pagination.current, pagination.pageSize);
      } else {
        message.error(res?.message || 'Failed to delete');
      }
    } catch (err) {
      message.error('Delete failed');
    }
  };

  const handleEdit = (record) => {
    setEditRecord(record);
    setModalVisible(true);
  };

  const handleCreate = () => {
    setEditRecord(null);
    setModalVisible(true);
  };

  const columns = [
    {
      title: 'Report Type',
      dataIndex: 'reportType',
      key: 'reportType',
      render: (type) => (
        <span style={{ fontWeight: 600, color: '#1a1a1a' }}>
          {REPORT_TYPE_LABELS[type] || type}
        </span>
      ),
    },
    {
      title: 'Frequency',
      dataIndex: 'frequency',
      key: 'frequency',
      render: (freq) => (
        <Tag
          color={FREQ_COLORS[freq] || 'default'}
          className="reg-schedule-freq-tag"
        >
          <ClockCircleOutlined /> {freq}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      render: (active) => (
        <Tag
          color={active !== false ? 'green' : 'default'}
          className="reg-status-tag"
        >
          {active !== false ? 'ACTIVE' : 'INACTIVE'}
        </Tag>
      ),
    },
    {
      title: 'Next Run',
      dataIndex: 'nextRunAt',
      key: 'nextRunAt',
      render: (ts) => (
        <span style={{ color: '#8c8c8c', fontSize: 12, fontWeight: 500 }}>
          {ts ? moment(ts).format('MMM DD, YYYY • hh:mm A') : '—'}
        </span>
      ),
    },
    {
      title: 'Last Run',
      dataIndex: 'lastRunAt',
      key: 'lastRunAt',
      render: (ts) => (
        <span style={{ color: '#8c8c8c', fontSize: 12, fontWeight: 500 }}>
          {ts ? moment(ts).format('MMM DD, YYYY • hh:mm A') : 'Never'}
        </span>
      ),
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (name) => <span style={{ fontWeight: 500 }}>{name || '—'}</span>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      render: (_, record) => (
        <div className="reg-action-row">
          <Button
            size="small"
            icon={<EditOutlined />}
            style={{ borderRadius: 6, fontSize: 11 }}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Delete this schedule?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button
              size="small"
              icon={<DeleteOutlined />}
              danger
              style={{ borderRadius: 6, fontSize: 11 }}
            />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
          style={{
            background: '#ff6700',
            borderColor: '#ff6700',
            borderRadius: 8,
            fontWeight: 700,
          }}
        >
          Create Schedule
        </Button>
      </div>

      <div className="reg-table-card">
        <Table
          columns={columns}
          dataSource={schedules}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} schedules`,
          }}
          onChange={handleTableChange}
          rowClassName={(_, idx) => (idx % 2 === 0 ? 'reg-row-even' : 'reg-row-odd')}
          className="reg-table"
        />
      </div>

      <ScheduleFormModal
        visible={modalVisible}
        onClose={() => { setModalVisible(false); setEditRecord(null); }}
        onSuccess={() => fetchSchedules(pagination.current, pagination.pageSize)}
        editRecord={editRecord}
      />
    </div>
  );
};

export default SchedulesTab;
